/* Matemática do storyboard das perguntas do checkout.
   Fica fora do componente para o fast-refresh não quebrar e para os
   invariantes serem testáveis sem montar React.

   A história é uma comparação: a MESMA pergunta, salva nos dois escopos.
   Primeiro "Todo pedido" (1 resposta), depois "Cada ingresso" (3 respostas).
   Só o escopo muda entre as duas metades — é isso que a tela não conta. */

/* ── Colunas (os três momentos) ── */
export const COLS = [
  { x: 14, w: 158, title: "Você cria a pergunta", sub: "no formulário do evento" },
  { x: 180, w: 158, title: "O comprador responde", sub: "no checkout, 3 ingressos" },
  { x: 346, w: 160, title: "Você lê a resposta", sub: "na aba Participantes" },
];
export const colCenter = (i: number) => COLS[i].x + COLS[i].w / 2;

/* A altura é enxuta de propósito: o storyboard divide a linha com o trilho de
   regras, e sobra vertical vira faixa branca no modal, não respiro. */
export const VIEW = { w: 520, h: 250 };
export const COL_Y = 12;
export const COL_H = 226;

/* ── Alvos do cursor (todos na coluna 1: só ali o produtor clica) ── */
export const QUESTION_FIELD = { x: 93, y: 73 };
export const SCOPE_ORDER = { x: 58, y: 108 };
export const SCOPE_ATTENDEE = { x: 127, y: 108 };
export const SAVE_BTN = { x: 93, y: 176 };
export const IDLE = { x: 36, y: 218 };

/* ── Geometria compartilhada com o desenho ──
   Mora aqui porque os voos dos chips precisam dela e os testes conferem que
   nada sai do quadro. */
export const ORDER_CARD = { x: 192, y: 56, w: 134, h: 42 };
/** Campo de resposta dentro do card do pedido — de onde os chips partem. */
export const ORDER_FIELD = { x: 259, y: 87 };
/** Topo de cada um dos três cartões de ingresso no checkout. */
export const TICKET_TOP = [104, 148, 192];
export const TICKET_H = 40;
/** Onde a linha da pergunta pousa dentro de cada cartão. */
export const chipTarget = (i: number) => ({ x: 259, y: TICKET_TOP[i] + 30.5 });
/** Tamanho do chip em voo — o teste de sobreposição depende dele. */
export const CHIP = { w: 56, h: 16 };

/** Texto digitado no campo da pergunta. */
export const QUESTION = "Tamanho da camiseta";
/** Nomes dos três participantes — os mesmos no checkout e na tabela. */
export const PEOPLE = ["Ana Souza", "Bruno Lima", "Carla Dias"];
/** Resposta única quando o escopo é o pedido. */
export const ORDER_ANSWER = "M";
/** Uma resposta por ingresso quando o escopo é o ingresso.
    A do meio repete de propósito: é a mesma pergunta, não outra. */
export const TICKET_ANSWERS = ["P", "M", "GG"];

/* ── Roteiro ── */
export type Beat =
  | { k: "typeQuestion" }
  | { k: "pickOrder" }
  | { k: "saveOrder" }
  | { k: "pickAttendee" }
  | { k: "saveAttendee" };

export const BEATS: Beat[] = [
  { k: "typeQuestion" },
  { k: "pickOrder" },
  { k: "saveOrder" },
  { k: "pickAttendee" },
  { k: "saveAttendee" },
];

export function beatTarget(b: Beat) {
  switch (b.k) {
    case "typeQuestion":
      return QUESTION_FIELD;
    case "pickOrder":
      return SCOPE_ORDER;
    case "saveOrder":
      return SAVE_BTN;
    case "pickAttendee":
      return SCOPE_ATTENDEE;
    case "saveAttendee":
      return SAVE_BTN;
  }
}

const CL = { reach: 620, press: 180, settle: 240, gap: 220 };

/** Folga extra depois de batidas que produzem algo para ler. */
const PAD: Partial<Record<Beat["k"], number>> = {
  typeQuestion: 900, // a pergunta é digitada
  pickOrder: 250,
  saveOrder: 2900, // o checkout monta, o comprador responde e a tabela enche
  pickAttendee: 400,
  saveAttendee: 3900, // a pergunta se multiplica nos três ingressos e a tabela troca
};

/* O tempo de percurso acompanha a distância: um pulo entre colunas leva mais
   que um ajuste de dois pixels. Sem isso o easeInOut, cuja velocidade de pico
   é 3× a média, joga o cursor a ~800px/s nos trechos longos. */
const REACH_REF = 150;
const REACH_MIN = 420;
const REACH_MAX = 1050;
const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(b.x - a.x, b.y - a.y);
const clampN = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Percurso da batida `i`, considerando de onde o cursor vem. */
export const reachAt = (i: number) => {
  const from = i === 0 ? IDLE : beatTarget(BEATS[i - 1]);
  return clampN((CL.reach * dist(from, beatTarget(BEATS[i]))) / REACH_REF, REACH_MIN, REACH_MAX);
};

export const beatDur = (i: number) => reachAt(i) + CL.press + CL.settle + CL.gap + (PAD[BEATS[i].k] ?? 0);

export const STARTS: number[] = [];
let acc = 0;
for (let i = 0; i < BEATS.length; i++) {
  STARTS.push(acc);
  acc += beatDur(i);
}
export const STORY_END = acc;
export const HOLD = 1700;
export const RESET = 700;
export const CYCLE = STORY_END + HOLD + RESET;
const FADE_IN = 420;

const idxOf = (k: Beat["k"]) => BEATS.findIndex((b) => b.k === k);
const TYPE_IDX = idxOf("typeQuestion");
const SAVE1_IDX = idxOf("saveOrder");
const SAVE2_IDX = idxOf("saveAttendee");

/* ── Tempos internos, relativos ao início da própria batida ── */
const TYPE_START = reachAt(TYPE_IDX) + CL.press + 100;
const TYPE_END = TYPE_START + 700;

/* Primeira gravação: o checkout monta antes de alguém responder, e a tabela
   só existe depois de existir resposta. Encher a tabela antes seria mentira. */
const S1_CLICK = reachAt(SAVE1_IDX) + CL.press;
const S1_ORDER_CARD = S1_CLICK + 220; // o bloco "Informações do pedido" aparece
const S1_TICKETS = S1_CLICK + 460; // e os três ingressos abaixo dele
const S1_ANSWER = S1_TICKETS + 620; // o comprador escolhe M
const S1_TABLE = S1_ANSWER + 620; // a coluna nasce na aba Participantes
const S1_CELLS = [S1_TABLE + 320, S1_TABLE + 520, S1_TABLE + 720];
const S1_NOTE = S1_CELLS[2] + 300;
const S1_HINT = S1_NOTE + 420;

/* Segunda gravação: o escopo vira "cada ingresso". O card do pedido se desfaz
   e a pergunta desce para dentro de cada ingresso — um chip por ingresso. */
const S2_CLICK = reachAt(SAVE2_IDX) + CL.press;
const S2_MORPH = S2_CLICK + 160; // card do pedido começa a sair
/* Um voo por vez: os três caminhos partem do mesmo ponto e o do ingresso 3
   passa por cima do destino do ingresso 1, então voos simultâneos viram dois
   chips empilhados. Separar por faixa não resolve — o chip tem 56px de largura
   e as faixas caberiam em 30. Sequencial também lê melhor: "e mais um". */
const S2_FLY_DUR = 520;
const S2_FLY_GAP = S2_FLY_DUR + 60;
const S2_FLY = [0, 1, 2].map((i) => S2_MORPH + 60 + i * S2_FLY_GAP);
const S2_ANSWERS = S2_FLY.map((t) => t + S2_FLY_DUR + 160);
const S2_SWAP = S2_ANSWERS[2] + 480; // e a tabela passa a mostrar três respostas
const S2_CELLS = [S2_SWAP, S2_SWAP + 200, S2_SWAP + 400];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const lerpVec = (a: { x: number; y: number }, b: { x: number; y: number }, t: number) => ({
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
});
const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const smoothstep = (t: number) => {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
};

export interface Frame {
  cursor: { x: number; y: number };
  press: number;
  clickPulse: number;
  showCursor: boolean;
  /** painel do produtor */
  typed: string;
  orderPicked: boolean;
  attendeePicked: boolean;
  savedPulse: number; // 0..1 — confirmação no botão salvar
  /** checkout */
  orderCardIn: number; // 0..1 — bloco "Informações do pedido"
  orderAnswerIn: number; // 0..1 — a resposta única
  ticketsIn: number; // 0..1 — os três cartões de ingresso
  /** 0..1 por ingresso — o chip da pergunta indo do pedido para o ingresso */
  chipFly: number[];
  /** 0..1 por ingresso — a linha da pergunta já instalada no cartão */
  rowIn: number[];
  /** 0..1 por ingresso — a resposta daquele ingresso */
  answerIn: number[];
  /** aba Participantes */
  tableIn: number; // 0..1 — cabeçalho e linhas vazias
  cellIn: number[]; // 0..1 — célula preenchida com a resposta do pedido
  cellSwap: number[]; // 0..1 — célula trocando para a resposta do ingresso
  noteMode: number; // 0..1 — cross-fade do texto que explica a coluna
  hintIn: number; // 0..1 — caixa tracejada (lote de fora e CSV)
  /** foco entre colunas e fade do ciclo */
  focus: number;
  stage: number;
  beat: number;
}

/** Em qual coluna a atenção está — muda dentro das batidas de salvar, porque
    é lá que a consequência atravessa as três colunas. */
function focusAt(e: number): number {
  const s1 = e - STARTS[SAVE1_IDX];
  const s2 = e - STARTS[SAVE2_IDX];
  // enquanto o cursor ainda está indo até o botão, a atenção continua na coluna 1
  if (s2 >= 0) return s2 >= S2_SWAP - 200 ? 2 : s2 >= S2_MORPH - 120 ? 1 : 0;
  if (e >= STARTS[idxOf("pickAttendee")]) return 0;
  if (s1 >= 0) return s1 >= S1_TABLE - 200 ? 2 : s1 >= S1_ORDER_CARD - 120 ? 1 : 0;
  return 0;
}

export function computeFrame(e: number): Frame {
  const stage = Math.min(smoothstep(e / FADE_IN), e > CYCLE - RESET ? smoothstep((CYCLE - e) / RESET) : 1);

  let index = BEATS.length - 1;
  let local = 0;
  if (e < STORY_END) {
    let i = 0;
    while (i < BEATS.length - 1 && e >= STARTS[i + 1]) i++;
    index = i;
    local = e - STARTS[i];
  } else {
    local = beatDur(index);
  }
  const reach = reachAt(index);

  const done = (k: Beat["k"]) => {
    const i = idxOf(k);
    // o efeito acontece no meio do clique, não no fim da batida
    return e >= STARTS[i] + reachAt(i) + CL.press * 0.6;
  };

  // cursor
  const b = BEATS[index];
  const target = beatTarget(b);
  const from = index === 0 ? IDLE : beatTarget(BEATS[index - 1]);
  let cursor = { ...target };
  let press = 0;
  let clickPulse = 0;

  if (e < STORY_END) {
    if (local < reach) {
      cursor = lerpVec(from, target, easeInOut(local / reach));
    } else if (local < reach + CL.press) {
      const t = (local - reach) / CL.press;
      press = easeOut(t);
      clickPulse = t;
    } else if (local < reach + CL.press + CL.settle) {
      press = 1 - easeOut((local - reach - CL.press) / CL.settle);
    }
  } else if (e < STORY_END + HOLD) {
    cursor = { ...beatTarget(BEATS[BEATS.length - 1]) };
  } else {
    cursor = lerpVec(beatTarget(BEATS[BEATS.length - 1]), IDLE, easeInOut((e - STORY_END - HOLD) / RESET));
  }

  // painel: a pergunta sendo digitada
  const typeLocal = e - STARTS[TYPE_IDX];
  const typeT = clamp01((typeLocal - TYPE_START) / (TYPE_END - TYPE_START));
  const typed = QUESTION.slice(0, Math.round(typeT * QUESTION.length));

  // checkout
  const s1 = e - STARTS[SAVE1_IDX];
  const s2 = e - STARTS[SAVE2_IDX];
  const morph = smoothstep((s2 - S2_MORPH) / 420);

  const orderCardIn = smoothstep((s1 - S1_ORDER_CARD) / 320) * (1 - morph);
  const orderAnswerIn = smoothstep((s1 - S1_ANSWER) / 300) * (1 - morph);
  const ticketsIn = smoothstep((s1 - S1_TICKETS) / 340);

  const chipFly = S2_FLY.map((t) => smoothstep((s2 - t) / S2_FLY_DUR));
  const rowIn = S2_FLY.map((t) => smoothstep((s2 - (t + S2_FLY_DUR - 220)) / 300));
  const answerIn = S2_ANSWERS.map((t) => smoothstep((s2 - t) / 280));

  // aba Participantes
  const tableIn = smoothstep((s1 - S1_TABLE) / 360);
  const cellIn = S1_CELLS.map((t) => smoothstep((s1 - t) / 280));
  const cellSwap = S2_CELLS.map((t) => smoothstep((s2 - t) / 300));
  const noteMode = smoothstep((s2 - S2_SWAP) / 360);
  const hintIn = smoothstep((s1 - S1_HINT) / 380);

  const savedPulse = Math.max(smoothstep((s1 - S1_CLICK) / 240), smoothstep((s2 - S2_CLICK) / 240));

  return {
    cursor,
    press,
    clickPulse,
    showCursor: e < STORY_END + HOLD,
    typed,
    orderPicked: done("pickOrder") && !done("pickAttendee"),
    attendeePicked: done("pickAttendee"),
    savedPulse,
    orderCardIn,
    orderAnswerIn,
    ticketsIn,
    chipFly,
    rowIn,
    answerIn,
    tableIn,
    cellIn,
    cellSwap,
    noteMode,
    hintIn,
    focus: focusAt(e),
    stage,
    beat: index,
  };
}

/** Posição do chip da pergunta em voo, do card do pedido para o ingresso `i`.
    O leque horizontal existe só para os três voos não virarem uma linha só. */
export const chipFlyPos = (i: number, t: number) => {
  const to = chipTarget(i);
  const bow = (i - 1) * 11;
  return {
    x: ORDER_FIELD.x + (to.x - ORDER_FIELD.x) * t + Math.sin(t * Math.PI) * bow,
    y: ORDER_FIELD.y + (to.y - ORDER_FIELD.y) * t,
  };
};

/** Peça em voo some nos últimos trechos, ao ser absorvida pelo destino. */
export const arrivalFade = (t: number) => 1 - Math.max(0, (t - 0.82) / 0.18);
